import classes from './index.module.css'
import classNames from 'classnames'
import {
  cloneElement,
  CSSProperties,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Badge, Checkbox, Tooltip } from 'antd'
import {
  CheckOutlined,
  EyeInvisibleOutlined,
  SwapOutlined,
} from '@ant-design/icons'

import {
  Cell,
  ColumnDef,
  ColumnPinningState,
  Header,
  Row,
  TableState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

// needed for table body level scope DnD setup
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'

// needed for row & cell level scope DnD setup
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { getAvatar } from '@/helpers/getAvatar'
import { RatingIcon, TelegramIcon, VKIcon, WhatsAppIcon } from '@/utils/icons'
import updateTableOrdering, { updateTableHiddenColumns } from './SettingsHelper'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useSelector } from 'react-redux'
import { detectLinkType, LinkIcon } from '@/helpers/new/detectLinkType'
import EmailVerifyWrapper from '@common/EmailVerifyWrapper/EmailVerifyWrapper'
import UserStatus from '@common/UserStatus/UserStatus'
import MissingFranchiseWarning from '@pages/Admin/AllUsers/components/MissingFranchiseWarning'
import { InputTextSaveCell } from '@pages/Admin/Reports/Payments/components/Cells/Cells'
import { updateUserRaring } from '@pages/Admin/Reports/UserRatingReport/utils/api'
import EditParentModal from "@pages/Admin/AllUsers/modals/EditParentModal";
import Modal from "@common/Modal";
import { useFranchises } from '@/hooks/useFranchises'
import { profileFieldsApi } from '@/api'
import {
  STUDENT_FIELD_TYPES,
  STUDENT_PARENT_FIELDS,
  STUDENT_PROFILE_SYSTEM_FIELDS,
  getStudentFieldDefaultActive,
  getStudentCustomFieldValues,
  getStudentProfileFields,
  isStudentProfileFieldVisibleForRole,
} from '@/helpers/studentProfileFields'
import {
  PROFILE_FIELD_ENTITY_TYPES,
  getProfileFieldValue,
  getProfileFieldsResponseData,
} from '@/helpers/profileFields'
import { getAgeFromBirthDate } from '@/helpers/date'
import StudentCourseIndicators from '@UI/New/StudentCourseIndicators'

type IUserClass = {
  id: number
  class_id: number
  user_id: number
  amount: string
  next_payment_date: string
  status_payment: number
  discount: number
  discount_currency: string
  deleted_at: string
  class: {
    id: number
    title: string
    one_pay: number
    amount: string
    created_at: number
    updated_at: number
    folder_ids: number
    ignore_payment: number
  }
}

type IDirection = {
  id: number
  name: string
  short_name: string
  description: string // JSON-строка, может быть разобрана в объект
  icon_path: string
  created_at: number
  updated_at: number
  code: string | null
  start_price: string
  use_promo_code: number
  use_discount: number
  filter_id: number
  seminarian_id: number
  permission_add_child: number
  view: number
  old_price: string
  sorting: number | null
  flag_easily_creating: number
}

type IOrder = {
  id: number
  direction_id: number
  children_id: number
  tariff_id: number
  learning_class: string | null
  teacher_id: string | null
  group_id: number
  buy_months: number
  buy_hours: string | null
  status_payment: number
  comment: string | null
  distribution: number
  payment_amount: string
  next_payment_date: string | null
  created_at: number
  updated_at: number
  countParentReports: number
  data_filled: number
  send_to_bot: number
  parent_touch: number
  is_disabled: number
  auto_renewal: boolean
  class_id: string | null
  favorite: string | null
  favorite_at: string | null
  disable_date: string | null
  disable_comment: string | null
  disabled_by_student: number
  direction: IDirection
}

interface UserProfile {
  experience: string | null
  about: string | null
  about_student: string | null
  address: string | null
  birth_date: string | null
  city: string | null
  education: string | null
  link_to_vk: string | null
  notes: string | null
  parent_name: string
  parent_email: string | null
  parent_phone: string | null
  parent_telegram: string | null
  parent_whatsapp: string | null
  profile_photo: string | null
  sex: string | null
  timezone: string | null
  learning_class: number | null
  custom_fields?: string | Record<string, unknown> | null
}

interface User {
  avatar_path: string
  balance: number
  balance_dollar: number
  balance_euro: number
  created_at: number
  custom_id: string
  email: string
  login?: string | null
  is_email_confirmed: number
  id: number
  name: string
  password: string
  phone: string
  position_rating: number | null
  last_login: string | null
  promo_code: string
  pulses: number
  gamification_balance?: number
  status: number
  updated_at: number
  userProfile: UserProfile
  orders: IOrder[]
  classUser: IUserClass[]
  parents?: { id: number; name: string; email: string | null; phone: string | null }[]
  university2035Linked?: boolean
  university2035UntiId?: string | null
  university2035LastSyncedAt?: string | null
  franchises?: { id: number; name: string }[]
  franchise_assignment_missing?: boolean
}



const StudentsTable = ({
  users = [],
  selected = [],
  onSelect = (user: User) => {},
  onUserClick = (user: User) => {},
  isEditMode = false,
  showUniversity2035 = false,
}: {
  users: User[]
  selected: User[]
  onSelect: (user: User) => void
  onUserClick: (user: User) => void
  isEditMode: boolean
  showUniversity2035: boolean
}) => {
  const columns = useTableColumns({
    selected,
    onSelect,
    onUserClick,
    onParentClick: (parentId: number) => setParentModal({ parentId }),
    showUniversity2035,
  })

  const addAlert = useAddAlert()
  const settings = useSelector((store: RootState) => store.settings)
  const defaultColumnOrdering = settings?.studentsTableOrdering
  const defaultHiddenColumns = settings?.studentsTableHiddenColumns
  const hideParentsData = settings?.hideParentsData == 1
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({})

  const [isNewOrdering, setIsNewOrdering] = useState<boolean>(false)
  const [isNewColumnVisibility, setIsNewColumnVisibility] =
    useState<boolean>(false)
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    columns.map((c) => c.id!),
  )
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: ['select'],
  })

  const updateDefaultColumnsOrdering = () => {
    try {
      const ordering = JSON.parse(defaultColumnOrdering ?? '') as unknown

      if (
        Array.isArray(ordering) &&
        ordering.every((item) => typeof item === 'string')
      ) {
        const uniqueElements = columnOrder.filter(
          (item) => !ordering.includes(item),
        )
        const newOrdering = ordering.concat(uniqueElements)
        setColumnOrder(newOrdering)
      } else {
        console.log('studentsTableOrdering -- wrong')
      }
    } catch (error) {
      console.log('studentsTableOrdering -- wrong')
    }
  }

  const updateDefaultHiddenColumns = () => {
    try {
      const hiddenColumns = JSON.parse(defaultHiddenColumns ?? '') as Record<
        string,
        boolean
      >

      if (hiddenColumns) {
        setColumnVisibility((prev) => {
          return { ...prev, ...hiddenColumns }
        })
      } else {
        console.log('studentsTableHiddenColumns -- wrong')
      }
    } catch (error) {
      console.log('studentsTableHiddenColumns -- wrong')
    }
  }

  useEffect(() => {
    updateDefaultColumnsOrdering()
    updateDefaultHiddenColumns()
  }, [])

  useEffect(() => {
    if (isEditMode !== false) return
    if (isNewOrdering) {
      updateTableOrdering(
        columnOrder,
        () => {
          addAlert({ text: 'Настройки обновлены!' })
          setIsNewOrdering(false)
        },
        () => {
          addAlert({ text: 'Ошибка.', type: 'error' })
        },
      )
    }
    if (isNewColumnVisibility) {
      updateTableHiddenColumns(
        columnVisibility,
        () => {
          addAlert({ text: 'Настройки обновлены!' })
          setIsNewColumnVisibility(false)
        },
        () => {
          addAlert({ text: 'Ошибка.', type: 'error' })
        },
      )
    }
  }, [isEditMode])

  const visibleColumns = useMemo(() => {
    return isEditMode
      ? columns.reduce<Record<string, boolean>>((acc, column) => {
          acc[column.id ?? ''] = true
          return acc
        }, {})
      : columnVisibility
  }, [isEditMode, columnVisibility])

  const table = useReactTable({
    data: users,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnOrder,
      columnPinning,
      columnVisibility: visibleColumns,
    },
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    // onColumnVisibilityChange: setColumnVisibility,
    // debugTable: true,
    // debugHeaders: true,
    // debugColumns: true,
  })

  // reorder columns after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    // Get indexes of active and over columns
    const oldIndex = columnOrder.indexOf(active.id as string)
    const newIndex = columnOrder.indexOf(over.id as string)

    // Prevent moving the first column
    if (oldIndex === 0 || newIndex === 0) {
      return
    }

    setIsNewOrdering(true)

    setColumnOrder((columnOrder) => {
      return arrayMove(columnOrder, oldIndex, newIndex)
    })
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const changeVisibility = (id: string) => {
    setIsNewColumnVisibility(true)
    setColumnVisibility({
      ...columnVisibility,
      [id]: !(columnVisibility[id ?? ''] ?? true),
    })
  }
  const [parentModal, setParentModal] = useState<{ parentId: number } | null>(null)
  return (
    // NOTE: This provider creates div elements, so don't nest inside of <table> elements
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div className={classes.tableWrapper}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className={classNames(
                  classes.rowHeaderWrapper,
                  classes.rowWrapper,
                )}
              >
                <SortableContext
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header, index) => (
                    <DraggableTableHeader
                      isEditMode={index !== 0 && isEditMode}
                      key={header.id}
                      header={header}
                      isVisible={columnVisibility[header.id ?? ''] ?? true}
                      onChangeVisibility={() => changeVisibility(header.id)}
                    />
                  ))}
                </SortableContext>
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={classes.rowWrapper}
                data-platform-training-student-id={row.original.id}
              >
                {row.getVisibleCells().map((cell, index) => (
                  <SortableContext
                    key={cell.id}
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    <DragAlongCell
                      isEditMode={index !== 0 && isEditMode}
                      key={cell.id}
                      cell={cell}
                      isVisible={columnVisibility[cell.column.id ?? ''] ?? true}
                      onClick={() => {
                        index !== 0 && onUserClick(row.original)
                      }}
                    />
                  </SortableContext>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal
          active={parentModal !== null}
          setActive={() => setParentModal(null)}
      >
        {parentModal && (
            <EditParentModal
                parentId={parentModal.parentId as any}
                onDismiss={() => setParentModal(null)}
                onUpdate={() => setParentModal(null)}
            />
        )}
      </Modal>
    </DndContext>
  )
}

const DraggableTableHeader = ({
  header,
  isEditMode = false,
  isVisible = true,
  onChangeVisibility = () => {},
}: {
  header: Header<any, unknown>
  isEditMode: boolean
  isVisible: boolean
  onChangeVisibility: () => void
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: !isEditMode,
    })

  const isPinned = header.column.getIsPinned()

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isPinned ? 'sticky' : 'relative',
    transform: isEditMode ? CSS.Translate.toString(transform) : '', // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    width: header.column.getSize(),
    cursor: isEditMode ? 'pointer' : 'default',
    zIndex: isPinned || isDragging ? 1 : 0,
    left:
      isPinned === 'left' ? `${header.column.getStart('left')}px` : undefined,
  }

  return (
    <th
      className={classNames(
        classes.headerCell,
        header.column.id === 'name' && classes.nameHeaderCell,
        !isVisible && classes.cellHidden,
      )}
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
      {isEditMode && (
        <>
          <SwapOutlined
            className={classes.headerCellSwapIcon}
            onPointerEnterCapture={null}
            onPointerLeaveCapture={null}
          />
          <EyeInvisibleOutlined
            className={classes.headerCellSwapIcon}
            onPointerDown={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onChangeVisibility()
            }}
            onPointerEnterCapture={null}
            onPointerLeaveCapture={null}
          />
        </>
      )}
    </th>
  )
}

const DragAlongCell = ({
  cell,
  isEditMode = false,
  onClick = undefined,
  isVisible = true,
}: {
  cell: Cell<any, User>
  isEditMode: boolean
  isVisible: boolean
  onClick?: () => void
}) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
    disabled: !isEditMode,
  })

  const isPinned = cell.column.getIsPinned()

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: isPinned ? 'sticky' : 'relative',
    transform: isEditMode ? CSS.Translate.toString(transform) : '', // translate instead of transform to avoid squishing
    transition: 'width transform 0.2s ease-in-out',
    width: cell.column.getSize(),
    zIndex: isPinned || isDragging ? 1 : 0,
    left: isPinned === 'left' ? `${cell.column.getStart('left')}px` : undefined,
  }

  return (
    <td
      style={style}
      ref={setNodeRef}
      className={classNames(isVisible === false && classes.cellHidden)}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  )
}

const SelectCell = ({ isSelected = false, onSelect = () => {} }) => {
  return (
    <div className={classes.numberedCell}>
      <Checkbox checked={isSelected} onClick={onSelect} style={{ zIndex: 0 }} />
    </div>
  )
}

const ImageCell = ({ user, alt = '' }: { user: User; alt: string }) => {
  return (
    <div className={classes.imageCell}>
      <img
        className={classes.userAvatar}
        src={getAvatar({ ...user, role: 'children' })}
        alt={alt}
      />
    </div>
  )
}

const TextCell = ({
  text = '',
  className = '',
  wrapperClassName = '',
  style = undefined,
  onClick = undefined,
}: {
  text: string
  className?: string
  wrapperClassName?: string
  style?: CSSProperties
  onClick?: () => void
}) => {
  return (
    <div
      className={classNames(classes.cellWrapper, wrapperClassName)}
      style={style}
    >
      <div
        className={classNames(classes.textCell, className)}
        onClick={onClick}
        style={{ cursor: onClick != undefined ? 'pointer' : 'default' }}
      >
        {text}
      </div>
    </div>
  )
}

const University2035Cell = ({ user }: { user: User }) => (
  <div className={classes.cellWrapper}>
    <div
      className={classNames(
        classes.university2035Badge,
        user.university2035Linked && classes.university2035BadgeLinked,
      )}
    >
      {user.university2035Linked ? 'Привязан' : 'Не привязан'}
    </div>
  </div>
)

const SocialLinkCell = ({ link }: { link: string }) => {
  const active = !!link
  const normalizedLink =
    active && !/^[a-z][a-z0-9+.-]*:/i.test(link) ? `https://${link}` : link

  return (
    <div className={classes.cellWrapper}>
      <div
        className={classes.linkCellWrapper}
        style={{ cursor: active ? 'pointer' : 'default' }}
        onClick={() => {
          active && window.open(normalizedLink, '_blank')
        }}
      >
        <LinkIcon
          link={normalizedLink}
                    style={{ fill: 'var(--color-button-primary-bg)' }}
          fallback={
            <div className={classNames(classes.textCell, classes.nameCell)}>
              {link}
            </div>
          }
        />
      </div>
    </div>
  )
}

export const LVLCell = ({
  className = '',
  inputClassName = '',
  user,
  label = '',
  role = 'children',
}: {
  user: User
  className?: string
  inputClassName?: string
  label?: string
  role?: string
}) => {
  const addAlert = useAddAlert()
  const [lvlState, setLvlState] = useState(user?.pulses ?? 0)

  const onUpdate = async () => {
    const res = (
      await updateUserRaring(user?.id, lvlState, user?.gamification_balance, role)
    ).success

    if (res) {
      user.pulses = lvlState
      addAlert({ title: 'Рейтинг обновлен' })
    } else {
      addAlert({
        title: 'Ошибка при сохранении рейтинга',
        type: 'error',
      })
    }
    return res
  }

  useEffect(() => {
    setLvlState(user?.pulses ?? 0)
  }, [user?.pulses])

  return (
    <div className={className}>
      <div className={classes.lvlCell}>
        {!!label && <span className={classes.ratingCellLabel}>{label}</span>}
        <RatingIcon style={{ minWidth: '24px', minHeight: '24px' }} />
        <Tooltip title={String(lvlState)}>
          <div className={classes.ratingValueEditor}>
            <InputTextSaveCell
              value={String(lvlState)}
              className={classNames(classes.inputTextSaveCell, inputClassName)}
              styles={{ backgroundColor: 'var(--color-button-neutral-bg)' }}
              onChange={(value) => {
                const num = Number(value)
                setLvlState(num)
              }}
              onSave={onUpdate}
              type="number"
            />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

export const GamificationBalanceCell = ({
  className = '',
  inputClassName = '',
  user,
  label = '',
  role = 'children',
}: {
  user: User
  className?: string
  inputClassName?: string
  label?: string
  role?: string
}) => {
  const addAlert = useAddAlert()
  const [balanceState, setBalanceState] = useState(
    user?.gamification_balance ?? user?.pulses ?? 0,
  )

  const onUpdate = async () => {
    const res = (
      await updateUserRaring(user?.id, user?.pulses ?? 0, balanceState, role)
    ).success

    if (res) {
      user.gamification_balance = balanceState
      addAlert({ title: 'Игровая валюта обновлена' })
    } else {
      addAlert({
        title: 'Ошибка при сохранении игровой валюты',
        type: 'error',
      })
    }
    return res
  }

  useEffect(() => {
    setBalanceState(user?.gamification_balance ?? user?.pulses ?? 0)
  }, [user?.gamification_balance, user?.pulses])

  return (
    <div className={className}>
      <div className={classes.lvlCell}>
        {!!label && <span className={classes.ratingCellLabel}>{label}</span>}
        <RatingIcon style={{ minWidth: '24px', minHeight: '24px' }} />
        <Tooltip title={String(balanceState)}>
          <div className={classes.ratingValueEditor}>
            <InputTextSaveCell
              value={String(balanceState)}
              className={classNames(classes.inputTextSaveCell, inputClassName)}
              styles={{ backgroundColor: 'var(--color-button-neutral-bg)' }}
              onChange={(value) => {
                const num = Number(value)
                setBalanceState(num)
              }}
              onSave={onUpdate}
              type="number"
            />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

const StatusCell = ({ status = 10 }: { status: number }) => {
  let statusText = ''
  let statusShortText = ''
  let className = ''

  switch (status) {
    case 10:
      statusText = 'Активен'
      statusShortText = 'А'
      className = classes.greenStatus
      break

    case 0:
      statusText = 'Не активен'
      statusShortText = 'Н'
      className = classes.redStatus
      break

    case 9:
      statusText = 'Удален'
      statusShortText = 'У'
      className = classes.greyStatus
      break
  }

  return (
    <div className={classes.cellWrapper}>
      <Tooltip title={statusText} mouseEnterDelay={0}>
        <div className={classNames(classes.statusCell, className)}>
          {statusShortText}
        </div>
      </Tooltip>
    </div>
  )
}

function formatTimestampDate(timestamp: number) {
  const date = new Date(timestamp * 1000)

  const formattedDate = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
  })

  return `${formattedDate}.${date.getFullYear().toString().slice(-2)}`
}

function formatLastActionTime(time?: string | null) {
  if (!time) {
    return '—'
  }

  const date = new Date(time)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day}.${month}.${year.toString().slice(-2)}`
}

function formatStringDate(date: string): string {
  const [year, month, day] = date.split('-')

  // Return the formatted date
  return `${day}.${month}.${year}`
}

const getStudentCardLink = (studentId: number) => {
  if (!studentId || typeof window === 'undefined') return ''

  const url = new URL('/users/students', window.location.origin)
  url.searchParams.set('student_id', String(studentId))

  return url.toString()
}

const getPaymentWithoutRegistrationLink = (studentId: number) => {
  if (!studentId || typeof window === 'undefined') return ''

  const url = new URL('/payment-without-registration', window.location.origin)
  url.searchParams.set('search', String(studentId))

  return url.toString()
}

interface TableColumnsProps {
  selected: User[]
  onSelect: (user: User) => void
  onUserClick: (user: User) => void
  onParentClick?: (parentId: number) => void
  showUniversity2035: boolean
}

interface SettingsState {
  studentsTableOrdering?: string
  studentsTableHiddenColumns?: string
  hideParentsData?: boolean | string | number
  isShowCityChild?: boolean | string
  isShowPhoneChild?: boolean | string
  isShowEmailChild?: boolean | string
  isShowGenderChild?: boolean | string
  isShowDateOfBirthChild?: boolean | string
  isShowClassChild?: boolean | string
  isShowPromoChild?: boolean | string
  isShowLinkToSocialMediaNetChild?: boolean | string
  isShowStudentCardLinkChild?: boolean | string
  isShowPaymentLinkChild?: boolean | string
  isShowUniversity2035Child?: boolean | string
  isShowIdChild?: boolean | string
  isShowPointerChild?: boolean | string
  isShowAboutMeChild?: boolean | string
  isShowAboutChild?: boolean | string
  isShowTimeZoneChild?: boolean | string
  isShowParentNameChild?: boolean | string
  isShowParentPhoneChild?: boolean | string
  isShowClassesChild?: boolean | string
  isShowCoursesChild?: boolean | string
  isShowAgreementsChild?: boolean | string
  isShowCustomFieldsChild?: boolean | string
  isShowParentEmailChild?: boolean | string
  requireStudentEmailConfirmation?: string
  customFieldsChild?: string | unknown[]
}

interface RootState {
  settings: SettingsState
  profile?: {
    profile?: {
      role?: string
    }
  }
}

const useTableColumns = ({
  selected,
  onSelect,
  onUserClick,
  onParentClick,
  showUniversity2035,
}: TableColumnsProps) => {
  const profileSettings = useSelector((store: RootState) => store.settings)
  const { enabled: franchisesEnabled } = useFranchises()
  const profileRole = useSelector(
    (store: RootState) => store.profile?.profile?.role,
  )
  const [customProfileFields, setCustomProfileFields] = useState<any[]>([])

  useEffect(() => {
    profileFieldsApi
      .index({ entity_type: PROFILE_FIELD_ENTITY_TYPES.STUDENT })
      .then((response) => {
        setCustomProfileFields(getProfileFieldsResponseData(response))
      })
      .catch(() => {
        setCustomProfileFields([])
      })
  }, [])

  return useMemo<ColumnDef<User>[]>(() => {
    const toBoolean = (value?: boolean | string): boolean => {
      return value === '1' || value === true
    }
    const profileFields = getStudentProfileFields(
      profileSettings,
      customProfileFields,
    )
    const defaultFieldById = [
      ...STUDENT_PROFILE_SYSTEM_FIELDS,
      ...STUDENT_PARENT_FIELDS,
    ].reduce((acc: Record<string, any>, field: any) => {
      acc[field.id] = field
      return acc
    }, {})
    const profileFieldById = profileFields.reduce(
      (acc: Record<string, any>, field: any) => {
        acc[field.id] = field
        return acc
      },
      {},
    )
    const isFieldVisible = (fieldId: string) => {
      const field = profileFieldById[fieldId]
      if (!field) {
        return (
          customProfileFields.length === 0 &&
          STUDENT_PARENT_FIELDS.some((item: any) => item.id === fieldId)
        )
      }
      return isStudentProfileFieldVisibleForRole(field, profileRole)
    }
    const isSettingVisible = (
      settingName: keyof SettingsState,
      fieldId: string,
    ) => {
      const field = defaultFieldById[fieldId]
      return (
        toBoolean(
          (profileSettings[settingName] as boolean | string | undefined) ??
            (getStudentFieldDefaultActive(field) ? '1' : '0'),
        ) && isFieldVisible(fieldId)
      )
    }
    const allColumns = [
      {
        header: '',
        id: 'select',
        size: 50,
        cell: ({ row }: { row: Row<User> }) => (
          <SelectCell
            isSelected={selected.some(
              (currentUser: User) => row.original.id === currentUser.id,
            )}
            onSelect={() => onSelect(row.original)}
          />
        ),
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <ImageCell user={row.original} alt={row.original.name} />
        ),
        header: 'Фото',
        id: 'avatar',
        size: 72,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <UserStatus
            user={row.original as any}
            style={{ width: '100%', height: '100%' }}
          >
            <div className={classes.nameContent}>
              <TextCell
                text={row.original.name?.trim() || 'Не указано'}
                className={classes.nameCell}
                wrapperClassName={classes.nameCellWrapper}
                onClick={() => onUserClick(row.original)}
              />
              <MissingFranchiseWarning
                visible={row.original.franchise_assignment_missing}
                entity="учащийся"
              />
              <StudentCourseIndicators student={row.original} compact />
            </div>
          </UserStatus>
        ),
        header: 'ФИО',
        id: 'name',
        size: 260,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <StatusCell status={row.original.status} />
        ),
        header: 'Статус',
        id: 'status',
        size: 72,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <University2035Cell user={row.original} />
        ),
        header: 'У2035',
        id: 'university2035',
        size: 140,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original.franchises?.map((item) => item.name).join(', ') ||
              'Не указан'
            }
          />
        ),
        header: 'Филиал',
        id: 'franchise',
        size: 180,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={formatLastActionTime(row.original?.last_login)} />
        ),
        header: 'Дата активности',
        id: 'last_active',
        size: 112,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <LVLCell className={classes.cellWrapper} user={row.original} />
        ),
        header: 'Опыт',
        id: 'lvl',
        size: 112,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <GamificationBalanceCell
            className={classes.cellWrapper}
            user={row.original}
          />
        ),
        header: 'Игровая валюта',
        id: 'gamification_balance',
        size: 132,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <EmailVerifyWrapper
            visible={
              !!row.original?.email &&
              profileSettings?.requireStudentEmailConfirmation === '1'
            }
            verified={row.original?.is_email_confirmed === 1}
            style={{ height: '100%', width: '100%', paddingRight: '12px' }}
          >
            <TextCell
              text={row.original.email?.toString() ?? '-'}
              className={classes.emailField}
              style={{ width: '100%' }}
            />
          </EmailVerifyWrapper>
        ),
        header: 'Почта',
        id: 'email',
        size: 220,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original.login?.toString() ?? '-'} />
        ),
        header: 'Логин',
        id: 'login',
        size: 124,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original.phone} />
        ),
        header: 'Телефон',
        id: 'phone',
        size: 136,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original.userProfile?.sex
                ? row.original.userProfile?.sex === 'male'
                  ? 'М'
                  : 'Ж'
                : '-'
            }
          />
        ),
        header: 'Пол',
        id: 'sex',
        size: 60,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original.userProfile?.birth_date
                ? formatStringDate(row.original.userProfile?.birth_date)
                : '-'
            }
          />
        ),
        header: 'ДР',
        id: 'birth_date',
        size: 92,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              getAgeFromBirthDate(row.original.userProfile?.birth_date)?.toString() ??
              '-'
            }
          />
        ),
        header: 'Возраст',
        id: 'class',
        size: 82,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <SocialLinkCell link={row.original.userProfile?.link_to_vk ?? ''} />
        ),
        header: 'Соцсеть',
        id: 'social_link',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original.userProfile?.city?.toString() ?? '-'} />
        ),
        header: 'Город',
        id: 'city',
        size: 112,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original.promo_code?.toString() ?? '-'} />
        ),
        header: 'Промокод',
        id: 'promo',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <SocialLinkCell link={getStudentCardLink(row.original.id)} />
        ),
        header: 'Ссылка на карточку',
        id: 'student_card_link',
        size: 220,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <SocialLinkCell link={getPaymentWithoutRegistrationLink(row.original.id)} />
        ),
        header: 'Ссылка на продление',
        id: 'payment_link',
        size: 220,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={row.original.id?.toString() ?? '-'}
            onClick={() => onUserClick(row.original)}
          />
        ),
        header: 'ID',
        id: 'id',
        size: 68,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original.custom_id?.toString() ?? '-'} />
        ),
        header: 'Указатель',
        id: 'custom_id',
        size: 104,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original.userProfile?.about?.toString() ?? '-'} />
        ),
        header: 'О себе',
        id: 'about',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={row.original.userProfile?.about_student?.toString() ?? '-'}
          />
        ),
        header: 'Об ученике',
        id: 'about_student',
      },
      {
        cell: ({ row }: { row: Row<User> }) => {
          const parents = row.original.parents ?? []
          if (!parents.length) return <TextCell text="Не указано" />
          return (
              <div className={classes.cellWrapper}>
                {parents.map((parent) => (
                    <div
                        key={parent.id}
                        className={classNames(classes.textCell, classes.nameCell)}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onParentClick?.(parent.id)}
                    >
                      {parent.name?.trim() || 'Не указано'}
                    </div>
                ))}
              </div>
          )
        },
        header: 'Фио родителя',
        id: 'parent_name',
        size: 180,
      },
      {
        cell: ({ row }: { row: Row<User> }) => {
          const parents = row.original.parents ?? []
          if (!parents.length) return <TextCell text="-" />
          return (
              <div className={classes.cellWrapper}>
                {parents.map((parent) => (
                    <div
                        key={parent.id}
                        className={classNames(classes.textCell, classes.nameCell)}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onParentClick?.(parent.id)}
                    >
                      {parent.email ?? '-'}
                    </div>
                ))}
              </div>
          )
        },
        header: 'Почта родителя',
        id: 'parent_email',
        size: 220,
      },
      {
        cell: ({ row }: { row: Row<User> }) => {
          const parents = row.original.parents ?? []
          if (!parents.length) return <TextCell text="-" />
          return (
              <div className={classes.cellWrapper}>
                {parents.map((parent) => (
                    <div
                        key={parent.id}
                        className={classNames(classes.textCell, classes.nameCell)}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onParentClick?.(parent.id)}
                    >
                      {parent.phone ?? '-'}
                    </div>
                ))}
              </div>
          )
        },
        header: 'Телефон родителя',
        id: 'parent_phone',
        size: 140,
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original.userProfile?.timezone
                ? row.original.userProfile?.timezone.toString() + 'ч.'
                : '-'
            }
          />
        ),
        header: 'Час.Пояс',
        id: 'gmt',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <SocialLinkCell
            link={
              row.original.userProfile?.parent_whatsapp ??
              row.original.userProfile?.parent_telegram ??
              ''
            }
          />
        ),
        header: 'Мессенджеры',
        id: 'parents_social_link',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original?.classUser?.filter((item) => !item.deleted_at)?.[0]
                ?.class?.title ?? '-'
            }
          />
        ),
        header: 'Класс №1',
        id: 'class1',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original?.classUser?.filter((item) => !item.deleted_at)?.[1]
                ?.class?.title ?? '-'
            }
          />
        ),
        header: 'Класс №2',
        id: 'class2',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original?.classUser?.filter((item) => !item.deleted_at)?.[2]
                ?.class?.title ?? '-'
            }
          />
        ),
        header: 'Класс №3',
        id: 'class3',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original?.classUser?.filter((item) => !item.deleted_at)?.[3]
                ?.class?.title ?? '-'
            }
          />
        ),
        header: 'Класс №4',
        id: 'class4',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              row.original?.classUser?.filter((item) => !item.deleted_at)?.[4]
                ?.class?.title ?? '-'
            }
          />
        ),
        header: 'Класс №5',
        id: 'class5',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original?.orders?.[0]?.direction?.name ?? '-'} />
        ),
        header: 'Курс №1',
        id: 'course1',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original?.orders?.[1]?.direction?.name ?? '-'} />
        ),
        header: 'Курс №2',
        id: 'course2',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original?.orders?.[2]?.direction?.name ?? '-'} />
        ),
        header: 'Курс №3',
        id: 'course3',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original?.orders?.[3]?.direction?.name ?? '-'} />
        ),
        header: 'Курс №4',
        id: 'course4',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell text={row.original?.orders?.[4]?.direction?.name ?? '-'} />
        ),
        header: 'Курс №5',
        id: 'course5',
      },
      {
        cell: ({ row }: { row: Row<User> }) => (
          <TextCell
            text={
              (JSON.parse(
                row.original?.userProfile?.notes ?? '[]',
              ) as string[][0]) ?? '-'
            }
          />
        ),
        header: 'Комментарий',
        id: 'note',
      },
    ]

    const customColumns = profileFields
      .filter(
        (field: any) =>
          field.isCustom &&
          isStudentProfileFieldVisibleForRole(field, profileRole),
      )
      .map((field: any) => ({
        cell: ({ row }: { row: Row<User> }) => {
          const value = getProfileFieldValue(
            getStudentCustomFieldValues(row.original),
            field,
          )
          const text = value === undefined || value === null ? '-' : String(value)

          if (field.type === STUDENT_FIELD_TYPES.LINK) {
            return <SocialLinkCell link={text === '-' ? '' : text} />
          }

          return <TextCell text={text} />
        },
        header: field.label,
        id: `custom_${field.name}`,
        size: field.type === STUDENT_FIELD_TYPES.TEXTAREA ? 260 : 180,
      }))

    const allColumnsWithCustom = [...allColumns, ...customColumns]

    const columnVisibilityMap: Record<string, boolean> = {
      city: isSettingVisible('isShowCityChild', 'city'),
      email: isSettingVisible('isShowEmailChild', 'email'),
      login: isFieldVisible('login'),
      phone: isSettingVisible('isShowPhoneChild', 'phone'),
      sex: isSettingVisible('isShowGenderChild', 'sex'),
      birth_date: isSettingVisible('isShowDateOfBirthChild', 'birthDate'),
      class: isSettingVisible('isShowClassChild', 'age'),
      promo: isSettingVisible('isShowPromoChild', 'promo'),
      social_link: isSettingVisible(
        'isShowLinkToSocialMediaNetChild',
        'socialLink',
      ),
      franchise: franchisesEnabled,
      student_card_link: isSettingVisible(
        'isShowStudentCardLinkChild',
        'studentCardLink',
      ),
      payment_link: isSettingVisible('isShowPaymentLinkChild', 'paymentLink'),
      id: isSettingVisible('isShowIdChild', 'id'),
      custom_id: isSettingVisible('isShowPointerChild', 'pointer'),
      about: isSettingVisible('isShowAboutMeChild', 'about'),
      about_student: isSettingVisible('isShowAboutChild', 'aboutStudent'),
      gmt: isSettingVisible('isShowTimeZoneChild', 'timezone'),
      parent_name:
        isSettingVisible('isShowParentNameChild', 'parentName') &&
        isFieldVisible('parents'),
      parent_phone:
        isSettingVisible('isShowParentPhoneChild', 'parentPhone') &&
        isFieldVisible('parents'),
      parent_email:
        isSettingVisible('isShowParentEmailChild', 'parentEmail') &&
        isFieldVisible('parents'),
      class1: isSettingVisible('isShowClassesChild', 'classes'),
      class2: isSettingVisible('isShowClassesChild', 'classes'),
      class3: isSettingVisible('isShowClassesChild', 'classes'),
      class4: isSettingVisible('isShowClassesChild', 'classes'),
      class5: isSettingVisible('isShowClassesChild', 'classes'),
      course1: isSettingVisible('isShowCoursesChild', 'courses'),
      course2: isSettingVisible('isShowCoursesChild', 'courses'),
      course3: isSettingVisible('isShowCoursesChild', 'courses'),
      course4: isSettingVisible('isShowCoursesChild', 'courses'),
      course5: isSettingVisible('isShowCoursesChild', 'courses'),
      university2035: showUniversity2035,
      // agreements: toBoolean(profileSettings.isShowAgreementsChild ?? true),
      // customFields: toBoolean(profileSettings.isShowCustomFieldsChild ?? true),
    }

    customColumns.forEach((column) => {
      columnVisibilityMap[column.id] = true
    })

    return allColumnsWithCustom.filter((column) =>
      column.id ? columnVisibilityMap[column.id] ?? true : true,
    )
  }, [
    selected,
    onSelect,
    onUserClick,
    profileSettings,
    franchisesEnabled,
    customProfileFields,
    profileRole,
    showUniversity2035,
  ])
}

export default StudentsTable
